import axios from 'axios';
import { API_BASE_URL, TENANT_SLUG } from './constants';
import {
  UserAddress,
  Order,
  OrderCreatePayload,
  User,
  LoginCredentials,
  RegisterCredentials,
  Product,
  Category,
  StorefrontCategory,
  UserFavorite,
  ReorderResponse
} from '../types';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(config => {
  // Add auth token
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Always add tenant header so the backend knows which tenant the request is for
  // This is essential for multi-tenant deployments where all frontends call the same backend
  if (TENANT_SLUG) {
    config.headers['X-Tenant-Slug'] = TENANT_SLUG;
  }

  return config;
});

const createCrud = <T, TCreate, TUpdate>(resource: string) => ({
    getAll: (params?: Record<string, any>): Promise<T[]> => api.get(`/${resource}`, { params }).then(res => res.data),
    getOne: (id: number): Promise<T> => api.get(`/${resource}/${id}`).then(res => res.data),
    create: (data: TCreate): Promise<T> => api.post(`/${resource}`, data).then(res => res.data),
    update: (id: number, data: TUpdate): Promise<T> => api.put(`/${resource}/${id}`, data).then(res => res.data),
    delete: (id: number): Promise<void> => api.delete(`/${resource}/${id}`).then(res => res.data),
});


// Auth API
export const register = async (userData: RegisterCredentials): Promise<User> => {
    const response = await api.post('/auth/register', userData);
    return response.data;
};

export const login = async (credentials: LoginCredentials): Promise<{ access_token: string }> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
};

export const getSelf = async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
};

export const updateUser = async (userData: Partial<User>): Promise<User> => {
    const response = await api.patch(`/auth/me`, userData);
    return response.data;
};

// Category API
const categoriesAPI = createCrud<Category, Omit<Category, 'id'>, Omit<Category, 'id'>>('categories');
export const getCategories = categoriesAPI.getAll;
export const getCategory = categoriesAPI.getOne;
export const createCategory = categoriesAPI.create;
export const updateCategory = categoriesAPI.update;
export const deleteCategory = categoriesAPI.delete;

export const getStorefrontCategories = async (): Promise<StorefrontCategory[]> => {
  const response = await api.get('/storefront-categories');
  const categories = (response.data || []) as Array<Omit<StorefrontCategory, 'date_created'> & { date_created: string | Date }>;

  return categories.map((category) => ({
    ...category,
    key_words: Array.isArray(category.key_words) ? category.key_words : [],
    date_created: category.date_created instanceof Date
      ? category.date_created
      : new Date(category.date_created),
  }));
};

// Product API
const productsAPI = createCrud<Product, Omit<Product, 'id'>, Omit<Product, 'id'>>('products');
export const getProducts = async (search?: string): Promise<Product[]> => {
  const products = await productsAPI.getAll(search ? { search } : {});
  return products.sort((a, b) => a.name.localeCompare(b.name));
};
export const getProduct = productsAPI.getOne;
export const createProduct = productsAPI.create;
export const updateProduct = productsAPI.update;
export const deleteProduct = productsAPI.delete;


// Address API
type AddressCreate = Omit<UserAddress, 'id' | 'user_id'>;
type AddressUpdate = Omit<UserAddress, 'id' | 'user_id'>;
const addressesAPI = createCrud<UserAddress, AddressCreate, AddressUpdate>('addresses');
export const getAddresses = addressesAPI.getAll;
export const getAddress = addressesAPI.getOne;
export const createAddress = addressesAPI.create;
export const updateAddress = addressesAPI.update;
export const deleteAddress = addressesAPI.delete;



// Order API
export const createOrder = async (orderData: OrderCreatePayload): Promise<Order> => {
  const response = await api.post('/orders', orderData);
  return response.data;
};

export const getOrders = async (): Promise<Order[]> => {
    const response = await api.get('/orders');
    return response.data;
};

export const getOrder = async (orderId: number): Promise<Order> => {
  const response = await api.get(`/orders/${orderId}`);
  return response.data;
};

// Favorites API
export const getFavorites = async (): Promise<UserFavorite[]> => {
  const response = await api.get('/favorites');
  return response.data;
};

export const addToFavorites = async (product: Product): Promise<UserFavorite> => {
  const response = await api.post('/favorites', {
    monorepo_reusable_type_id: product.id,
    product_snapshot: product
  });
  return response.data;
};

export const removeFromFavorites = async (favoriteId: number): Promise<void> => {
  await api.delete(`/favorites/${favoriteId}`);
};

export const removeProductFromFavorites = async (productId: number): Promise<void> => {
  await api.delete(`/favorites/product/${productId}`);
};

export const checkIfFavorite = async (productId: number): Promise<{ is_favorite: boolean }> => {
  const response = await api.get(`/favorites/check/${productId}`);
  return response.data;
};

// Reorder API
export const reorderFromPrevious = async (orderId: number): Promise<ReorderResponse> => {
  const response = await api.post(`/orders/${orderId}/reorder`);
  return response.data;
};
